import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import { CircularProgress } from "@mui/material";
import { formatDistanceToNow } from "date-fns";
import { fromZonedTime, toZonedTime } from "date-fns-tz";
import { Capacitor } from "@capacitor/core";
import { Geolocation } from '@capacitor/geolocation';
import FlashDurations from "./FlashDurations";
import Comments from "./FlashComments";
import { IconButton, Drawer, Popover, Typography, List, ListItem } from "@mui/material";
import PublicIcon from "@mui/icons-material/Public";
import "../styles/flashes.css";

const Flash = () => {
  const { flashId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [flashData, setFlashData] = useState(null);

  const [anchorEl, setAnchorEl] = useState(null);
  const [taggedUsers, setTaggedUsers] = useState([]);

  const [userLocation, setUserLocation] = useState(null);
  const [locationPermissionGranted, setLocationPermissionGranted] = useState(null);
  const [distanceCache, setDistanceCache] = useState({});

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  useEffect(() => {
    const fetchUserLocation = async () => {
      try {
        if (Capacitor.getPlatform() === "web") {
          if ("geolocation" in navigator) {
            navigator.geolocation.getCurrentPosition(
              (position) => {
                console.log("User's location:", position);
                setUserLocation({
                  latitude: position.coords.latitude,
                  longitude: position.coords.longitude,
                });
                setLocationPermissionGranted(true);
              },
              (error) => {
                console.error("Error fetching user location:", error);
                setLocationPermissionGranted(false);
              }
            );
          }
          else {
            console.error("Geolocation is not supported by this browser.");
            setLocationPermissionGranted(false);
          }
        }
        else {
          const permission = await Geolocation.requestPermissions();
          if (permission.location === "granted") {
            const position = await Geolocation.getCurrentPosition();
            setUserLocation({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            });
            setLocationPermissionGranted(true);
          }
          else {
            setLocationPermissionGranted(false);
          }
        }
      } catch (err) {
        console.error("Error fetching user location:", err);
      }
    };

    fetchUserLocation();
  }, []);

  useEffect(() => {
    const fetchSingleFlash = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get("/fetch/flashes", {
          params: { flash_id: flashId },
        });

        const places = response.data.places;

        if (places && places.length > 0) {
          const place = places[0];
          const post = place.posts[0];
          setFlashData({ ...post, place_name: place.place_name, country: place.country });
        }
        else {
          setFlashData(null);
        }
      } catch (error) {
        console.error("Error fetching the flash:", error);
        setFlashData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchSingleFlash();
  }, [flashId]);

  const handleLike = async (flash_owner, is_liked) => {
    try {
      if(is_liked) {
        await axiosInstance.delete(`/flash-likes`, { data: { flash_id: flashId } });
      }
      else {
        await axiosInstance.post(`/flash-likes`, { flash_id: flashId, flash_owner });
      }

      setFlashData((prevFlashData) => {
        if (!prevFlashData) return prevFlashData;
    
        return {
          ...prevFlashData,
          is_liked: !prevFlashData.is_liked,
        };
      });
    } catch (err) {
      console.error("Error toggling like:", err);
    }
  };

  const handleOpenPopover = (event, tags) => {
    setAnchorEl(event.currentTarget);
    setTaggedUsers(tags);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
    setTaggedUsers([]);
  };

  const isPopoverOpen = Boolean(anchorEl);

  const openDrawer = () => {
    setIsDrawerOpen(true);
    document.querySelector(".bottom-nav")?.classList.add("hidden");
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    document.querySelector(".bottom-nav")?.classList.remove("hidden");
  };

  const redirectToGoogleMaps = (latitude, longitude) => {
    const googleMapsUrl = `https://www.google.com/maps?q=${latitude},${longitude}`;
    window.open(googleMapsUrl, "_blank");
  };

  const getTimeAgo = (utcTimestamp) => {
    const utcDate = fromZonedTime(new Date(utcTimestamp), "UTC");
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const localTime = toZonedTime(utcDate, userTimeZone);

    return formatDistanceToNow(localTime, { addSuffix: true });
  };

  if (loading) {
    return (
      <div className="loading-container">
        <CircularProgress size={40} sx={{ color: 'black' }} />
      </div>
    );
  }

  if (!flashData) {
    return (
      <div className="loading-container">
        <p style={{ fontSize: "18px", color: "#555", textAlign: "center" }}>
          Flash not found.
        </p>
      </div>
    );
  }

  const {
    username,
    profile_url,
    content_url,
    isVideo,
    place_name,
    country,
    created_at,
    tags,
  } = flashData;

  const timeAgo = getTimeAgo(created_at);

  return (
    <div style={{ marginTop: "35px" }}>
      <h2 className="flash-title">{place_name}</h2>

      <img
        src={`/assets/flags/${country}.svg`}
        alt={country}
        className="country-flag"
        style={{ top: "42px" }} // 32px
      />

      {locationPermissionGranted && (
        <FlashDurations
          latitude={flashData.latitude}
          longitude={flashData.longitude}
          userLocation={userLocation}
          cache={distanceCache}
          setCache={setDistanceCache}
        />
      )}

      <div className="flash-container">
        {tags && tags.length > 0 && (
          <span
            className="tag-span"
            onClick={(e) => handleOpenPopover(e, tags)}
          >
            +{tags.length}
          </span>
        )}
        <img
          src={profile_url || "/assets/images/default_profile.png"}
          alt={username}
          onClick={(e) => (tags && tags.length > 0) ? handleOpenPopover(e, tags) : navigate(`/profile/${flashData.user_id}`)}
          className="flash-profile-img"
        />
        <div className="inner-div">
          <span 
            className="flash-username-span"
            onClick={() => navigate(`/profile/${flashData.user_id}`)}
          >
            {username}
          </span>
          <span className="flash-timestamp-span">{timeAgo}</span>
        </div>

        <Popover
          open={isPopoverOpen}
          anchorEl={anchorEl}
          onClose={handleClosePopover}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          sx={{
            "& .MuiPopover-paper": {
              backgroundColor: "#f9f9f9",
              boxShadow: "0 0 2px rgba(142, 142, 142, 0.1)",
              borderRadius: "10px",
              padding: "0",
              minWidth: "200px",
            },
          }}
        >
          <List>
            {taggedUsers.map((tag) => (
              <ListItem key={tag.tag_id} onClick={() => navigate(`/profile/${tag.user_id}`)}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#333",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <img
                    src={tag.profile_url || "/assets/images/default_profile.png"}
                    alt={tag.username}
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      objectFit: "cover",
                      cursor: "pointer",
                    }}
                  />
                  {tag.username}
                </Typography>
              </ListItem>
            ))}
          </List>
        </Popover>
      </div>

      <div className="flash-content">
        {isVideo ? (
          <video
            src={content_url}
            loop
            playsInline
            style={{ width: '100%', height: 'auto' }}
          />
        ) : (
          <img
            src={content_url}
            alt="Flash"
            style={{ width: '100%', height: 'auto' }}
          />
        )}
      </div>

      <div className="flash-icons">
        <div className="inner-flash-icons">
          <button
            onClick={() =>
              handleLike(flashData.user_id, flashData.is_liked)
            }
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              outline: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <i
              className={`fa${flashData.is_liked ? "s" : "r"} fa-heart`}
              style={{
                fontSize: "28px",
                color: flashData.is_liked ? "#ef4444" : "#6b7280",
              }}
            ></i>
          </button>

          <button
            onClick={openDrawer}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              outline: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <i
              className="far fa-comment"
              style={{
                fontSize: "28px",
                color: "#6b7280",
              }}
            ></i>
          </button>

          <IconButton
            onClick={() =>
              redirectToGoogleMaps(flashData.latitude, flashData.longitude)
            }
            style={{
              padding: "0 2px",
              background: "none",
              border: "none",
              cursor: "pointer",
              outline: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PublicIcon
              style={{
                fontSize: "28px",
                color: "#6b7280",
                cursor: "pointer",
              }}
            />
          </IconButton>
        </div>
      </div>

      <Drawer
        anchor="bottom"
        open={isDrawerOpen}
        onClose={closeDrawer}
        PaperProps={{
          style: { borderTopLeftRadius: 20, borderTopRightRadius: 20 },
        }}
      >
        {flashId && <Comments flashId={flashId} onClose={closeDrawer} />}
      </Drawer>
      
    </div>
  );
};

export default Flash;